import type {
  AbilityDefinition,
  DeveloperConfig,
  DeveloperLevel,
  PrestigeUpgradeDefinition,
  RandomEventDefinition,
  StrategicDebtItem,
  SupportRole,
  SupportRoleConfig,
  TradeoffModeDefinition,
  UpgradeDefinition,
} from "./types.js";

export const STORAGE_KEY = "loc-clicker.state.v1";
export const STORAGE_BACKUP_KEY = `${STORAGE_KEY}.backup`;

export const DEVELOPER_LEVELS: Record<DeveloperLevel, DeveloperConfig> = {
  junior: {
    label: "Jr Developer",
    hireCostLoc: 18,
    locPerSecond: 0.7,
  },
  mid: {
    label: "Mid Developer",
    hireCostLoc: 110,
    locPerSecond: 2.5,
  },
  senior: {
    label: "Sr Developer",
    hireCostLoc: 520,
    locPerSecond: 8.5,
  },
  architect: {
    label: "Architect",
    hireCostLoc: 1200,
    locPerSecond: 5.2,
  },
};

export const PRODUCT_TEAM_ROLES: Record<SupportRole, SupportRoleConfig> = {
  product: {
    label: "Product Manager",
    hireCostLoc: 260,
  },
  ux: {
    label: "UX Designer",
    hireCostLoc: 240,
  },
  sre: {
    label: "SRE",
    hireCostLoc: 300,
  },
};

export const AI_TOKEN_LOC_COST = 16;
export const AI_BOOST_DURATION_MS = 15_000;
export const AI_BOOST_MULTIPLIER = 1.8;
export const AI_BOOST_BUG_RISK_BONUS = 0.9;
export const AI_AGENT_LOC_PER_SECOND = 5.5;
export const AI_AGENT_BUG_PRESSURE_WEIGHT = 2.4;

export const BUG_INTERVAL_PER_DEV_MS = 60_000;
export const BASE_RANDOM_BUG_CHANCE_PER_DEV_SECOND = 0.012;
export const BUG_SEVERITY_MIN = 0.06;
export const BUG_SEVERITY_MAX = 0.16;
export const MIN_PRODUCTION_MULTIPLIER = 0.1;
export const TECH_DEBT_REPAIR_BASE_COST = 120;
export const TECH_DEBT_REPAIR_LOC_TO_DEBT_FACTOR = 1.35;
export const TECH_DEBT_STRATEGIC_CHECK_INTERVAL_MS = 45_000;

export const BUG_TITLES: string[] = [
  "Null Pointer Panic",
  "Off-by-One Apocalypse",
  "Race Condition Relay",
  "Infinite Spinner Syndrome",
  "Ghost Click Incident",
  "Timezone Drift Disaster",
  "Cache Invalidation Mystery",
  "Phantom Promise Rejection",
  "Hydration Mismatch Chaos",
  "NaN Propagation Event",
  "Memory Leak Lagoon",
  "Zombie WebSocket",
  "Retry Storm",
  "Orphaned Timeout",
  "Stale Closure Surprise",
  "Floating Point Gremlin",
  "Session Cookie Vanishing Act",
  "CORS Preflight Meltdown",
  "Deadlock at Dawn",
  "Merge Conflict Echo",
  "Mutex Mayhem",
  "UI Thread Freeze",
  "Unexpected Undefined",
  "Circular Dependency Ouroboros",
  "Schema Drift",
  "Duplicate Key Revolt",
  "Out-of-Order Event Saga",
  "N+1 Query Avalanche",
  "Misspelled Env Var",
  "TLS Handshake Hiccup",
  "Disk Full Plot Twist",
  "Webhook Whiplash",
  "Queue Backpressure Blowup",
  "Feature Flag Flip-Flop",
  "A11y Linter Revolt",
  "Index Out of Bounds",
  "UTF-8 Mojibake Attack",
  "Flaky Test Poltergeist",
  "Broken Source Map",
  "Recursive Render Loop",
  "Botched Migration",
  "Staging Config Leak",
  "Cookie Crumble",
  "Dangling Pointer Drama",
  "Monorepo Monsoon",
  "Dependency Hellfire",
  "Semver Betrayal",
  "Compiler Tantrum",
  "CSS Specificity War",
  "Hydra State Regression",
  "Brittle Snapshot Failure",
  "Proxy Timeout Spiral",
  "Dark Mode Paradox",
  "Rubber Duck Mutiny",
  "Ping-Pong Table Incident",
  "The TODO That Went to Production",
  "Left Pad Regression",
  "Merge Button Misfire",
  "Keyboard Shortcut Ambush",
  "Cowboy Deploy",
  "Oops-All-Console-Logs",
  "Weekend Hotfix Roulette",
  "Blame Annotation Blackout",
  "The 2am “Quick Fix”",
  "Copy-Paste Catastrophe",
  "Regex of Doom",
  "Broken Build Cache",
  "Kubernetes Kubefumble",
  "SRE Pager Symphony",
  "Prompt Injection Side Quest",
  "AI Hallucination Commit",
  "Agentic Refactor Gone Wild",
  "Deleted The Wrong Branch",
  "Undo Stack Tragedy",
  "Retroaction Item Overload",
  "Accidental Global Replace",
  "Spicy Refactor",
  "Microservice Message Loss",
  "Eventual Consistency Eventually",
  "SDK Version Schism",
  "Infinite Redirect Vortex",
  "Sticky Session Slip",
  "Non-Deterministic Nightmare",
  "Trailing Comma Panic",
  "Cron Job Time Warp",
  "Cold Start Surprise",
  "State Machine Identity Crisis",
  "A/B Test Contamination",
  "Canary Fell Over",
  "Blue-Green Confusion",
  "Read Replica Mirage",
  "Observability Blind Spot",
  "Metric Cardinality Explosion",
  "Heap Snapshot Horror",
  "Garbage Collector Jitters",
  "Serialization Mismatch",
  "Branch Protection Bypass",
  "Keyboard Cat Walked on CI",
  "Works on My Machine",
];

export const STRATEGIC_TECH_DEBT_ITEMS: StrategicDebtItem[] = [
  {
    id: "rewrite-renderer",
    title: "Rewrite Rendering Component",
    description: "Rendering module is brittle and blocks feature delivery.",
    rewriteCostLoc: 420,
    postponeDebtPenalty: 180,
  },
  {
    id: "replace-legacy-auth",
    title: "Refactor Legacy Auth Gateway",
    description: "Auth edge cases keep leaking into production.",
    rewriteCostLoc: 520,
    postponeDebtPenalty: 220,
  },
  {
    id: "untangle-state-store",
    title: "Rewrite State Management Layer",
    description: "State coupling causes cascading regressions.",
    rewriteCostLoc: 470,
    postponeDebtPenalty: 200,
  },
  {
    id: "split-monolith-module",
    title: "Split Monolith Billing Module",
    description: "Billing codebase is too complex to maintain safely.",
    rewriteCostLoc: 560,
    postponeDebtPenalty: 240,
  },
  {
    id: "replace-job-runner",
    title: "Rewrite Background Job Runner",
    description: "Retries and dead letters are out of control.",
    rewriteCostLoc: 500,
    postponeDebtPenalty: 210,
  },
];

export const PRESTIGE_LOC_THRESHOLD = 1_000_000;
export const PRESTIGE_LOC_SCALING = 1.85;

export const UPGRADE_CATALOG: UpgradeDefinition[] = [
  {
    id: "better_keyboard",
    name: "Better Keyboard",
    category: "Click Upgrades",
    costLoc: 25,
    maxLevel: 1,
    description: "+1 LOC per click",
  },
  {
    id: "mechanical_keyboard",
    name: "Mechanical Keyboard",
    category: "Click Upgrades",
    costLoc: 90,
    maxLevel: 1,
    description: "+5 LOC per click",
  },
  {
    id: "ide_autocomplete",
    name: "IDE Autocomplete",
    category: "Click Upgrades",
    costLoc: 220,
    maxLevel: 1,
    description: "+10 LOC per click",
  },
  {
    id: "code_snippets",
    name: "Code Snippets",
    category: "Click Upgrades",
    costLoc: 400,
    maxLevel: 5,
    description: "+12% LOC per click per level",
  },
  {
    id: "ai_pair_programmer",
    name: "AI Pair Programmer",
    category: "Click Upgrades",
    costLoc: 550,
    maxLevel: 3,
    description: "Clicks add extra LOC/sec aura for 5s",
  },
  {
    id: "refactoring_tools",
    name: "Refactoring Tools",
    category: "Click Upgrades",
    costLoc: 500,
    maxLevel: 3,
    description: "Clicks reduce bug severity slightly",
  },

  {
    id: "junior_training_program",
    name: "Junior Training Program",
    category: "Developer Upgrades",
    costLoc: 350,
    maxLevel: 3,
    description: "+20% junior LOC/sec per level",
  },
  {
    id: "senior_architecture_review",
    name: "Senior Architecture Review",
    category: "Developer Upgrades",
    costLoc: 900,
    maxLevel: 2,
    description: "+10% all dev LOC per level",
  },
  {
    id: "agile_transformation",
    name: "Agile Transformation",
    category: "Developer Upgrades",
    costLoc: 1200,
    maxLevel: 2,
    description: "+5% per active dev type per level",
  },
  {
    id: "better_coffee",
    name: "Better Coffee",
    category: "Developer Upgrades",
    costLoc: 700,
    maxLevel: 3,
    description: "+15% productivity per level",
  },
  {
    id: "team_leads",
    name: "Team Leads",
    category: "Developer Upgrades",
    costLoc: 1600,
    maxLevel: 10,
    description: "Each lead boosts up to 5 devs",
  },
  {
    id: "department_structure",
    name: "Department Structure",
    category: "Developer Upgrades",
    costLoc: 2100,
    maxLevel: 5,
    description: "+1% LOC per dev per level",
  },
  {
    id: "hiring_pipeline",
    name: "Hiring Pipeline",
    category: "Developer Upgrades",
    costLoc: 1300,
    maxLevel: 4,
    description: "Devs cost less LOC",
  },

  {
    id: "larger_context_window",
    name: "Larger Context Window",
    category: "AI Token Upgrades",
    costLoc: 1200,
    maxLevel: 3,
    description: "Boosts last longer",
  },
  {
    id: "better_models",
    name: "Better Models",
    category: "AI Token Upgrades",
    costLoc: 1800,
    maxLevel: 3,
    description: "Boost multiplier increases",
  },
  {
    id: "prompt_templates",
    name: "Prompt Templates",
    category: "AI Token Upgrades",
    costLoc: 1100,
    maxLevel: 4,
    description: "AI agents are cheaper",
  },
  {
    id: "ai_debugging",
    name: "AI Debugging",
    category: "AI Token Upgrades",
    costLoc: 2000,
    maxLevel: 3,
    description: "Fewer bugs during boosts",
  },

  {
    id: "unit_tests",
    name: "Unit Tests",
    category: "Bug System Upgrades",
    costLoc: 1000,
    maxLevel: 3,
    description: "-20% bug chance per level",
  },
  {
    id: "static_analysis",
    name: "Static Analysis",
    category: "Bug System Upgrades",
    costLoc: 1400,
    maxLevel: 3,
    description: "-10% bug severity per level",
  },
  {
    id: "code_reviews",
    name: "Code Reviews",
    category: "Bug System Upgrades",
    costLoc: 1300,
    maxLevel: 2,
    description: "Fixing a bug may resolve extra bugs",
  },
  {
    id: "type_system",
    name: "Type System",
    category: "Bug System Upgrades",
    costLoc: 1800,
    maxLevel: 2,
    description: "Fewer bugs per dev",
  },
  {
    id: "ci_pipeline",
    name: "CI Pipeline",
    category: "Bug System Upgrades",
    costLoc: 1700,
    maxLevel: 2,
    description: "Auto-fix minor bugs",
  },
  {
    id: "monitoring",
    name: "Monitoring",
    category: "Bug System Upgrades",
    costLoc: 1500,
    maxLevel: 2,
    description: "Early bug detection lowers severity",
  },
  {
    id: "sre_team",
    name: "SRE Team",
    category: "Bug System Upgrades",
    costLoc: 2200,
    maxLevel: 3,
    description: "Bugs decay over time",
  },

  {
    id: "tech_debt_cleanup",
    name: "Tech Debt Cleanup",
    category: "Meta Productivity Upgrades",
    costLoc: 2100,
    maxLevel: 5,
    description: "+10% global LOC per level",
  },
  {
    id: "microservices",
    name: "Microservices",
    category: "Meta Productivity Upgrades",
    costLoc: 2600,
    maxLevel: 3,
    description: "+6% per active dev type per level",
  },
  {
    id: "monorepo",
    name: "Monorepo",
    category: "Meta Productivity Upgrades",
    costLoc: 2000,
    maxLevel: 2,
    description: "Faster builds and less context switching",
  },
  {
    id: "build_cache",
    name: "Build Cache",
    category: "Meta Productivity Upgrades",
    costLoc: 1800,
    maxLevel: 5,
    description: "+3 LOC/sec per level",
  },
  {
    id: "unlock_mid_developers",
    name: "Unlock Mid Developers",
    category: "Meta Productivity Upgrades",
    costLoc: 320,
    maxLevel: 1,
    description: "Allows hiring mid developers",
  },
  {
    id: "unlock_senior_developers",
    name: "Unlock Senior Developers",
    category: "Meta Productivity Upgrades",
    costLoc: 1400,
    maxLevel: 1,
    requires: ["unlock_mid_developers"],
    description: "Allows hiring senior developers",
  },
  {
    id: "unlock_architect",
    name: "Unlock Architect",
    category: "Meta Productivity Upgrades",
    costLoc: 1800,
    maxLevel: 1,
    requires: ["unlock_senior_developers"],
    description: "Allows hiring software architects",
  },

  {
    id: "unlock_crunch_time",
    name: "Crunch Time",
    category: "Active Abilities",
    costLoc: 2500,
    maxLevel: 1,
    description: "x3 LOC for 30 seconds (45s cooldown)",
  },
  {
    id: "unlock_hackathon",
    name: "Hackathon",
    category: "Active Abilities",
    costLoc: 2800,
    maxLevel: 1,
    description: "Temporary dev team for 30 seconds (60s cooldown)",
  },
  {
    id: "unlock_coffee_break",
    name: "Coffee Break",
    category: "Active Abilities",
    costLoc: 1700,
    maxLevel: 1,
    description: "x1.6 dev speed for 20 seconds (40s cooldown)",
  },
  {
    id: "unlock_bug_bash",
    name: "Bug Bash",
    category: "Active Abilities",
    costLoc: 2200,
    maxLevel: 1,
    description: "Fix all bugs instantly (50s cooldown)",
  },

  {
    id: "faster_ci",
    name: "Faster CI",
    category: "Infrastructure Upgrades",
    costLoc: 2000,
    maxLevel: 4,
    description: "+4 LOC/sec per level",
  },
  {
    id: "bigger_servers",
    name: "Bigger Servers",
    category: "Infrastructure Upgrades",
    costLoc: 2300,
    maxLevel: 3,
    description: "Improves scaling for large teams",
  },
  {
    id: "kubernetes",
    name: "Kubernetes",
    category: "Infrastructure Upgrades",
    costLoc: 2800,
    maxLevel: 3,
    description: "Extra scaling bonus per developer",
  },
  {
    id: "observability",
    name: "Observability",
    category: "Infrastructure Upgrades",
    costLoc: 1900,
    maxLevel: 3,
    description: "Reduces bug chance",
  },

  {
    id: "rubber_duck_debugging",
    name: "Rubber Duck Debugging",
    category: "Humor Upgrades",
    costLoc: 900,
    maxLevel: 3,
    description: "Bugs decay faster",
  },
  {
    id: "stackoverflow_subscription",
    name: "StackOverflow Subscription",
    category: "Humor Upgrades",
    costLoc: 800,
    maxLevel: 5,
    description: "Click bonus",
  },
  {
    id: "dark_mode",
    name: "Dark Mode",
    category: "Humor Upgrades",
    costLoc: 500,
    maxLevel: 1,
    description: "+5% productivity",
  },
  {
    id: "standing_desk",
    name: "Standing Desk",
    category: "Humor Upgrades",
    costLoc: 950,
    maxLevel: 3,
    description: "+2% dev output per level",
  },
  {
    id: "ping_pong_table",
    name: "Ping-Pong Table",
    category: "Humor Upgrades",
    costLoc: 1200,
    maxLevel: 3,
    description: "+1% global morale per level",
  },
  {
    id: "product_discovery_framework",
    name: "Product Discovery Framework",
    category: "Meta Productivity Upgrades",
    costLoc: 1700,
    maxLevel: 4,
    description: "Product people improve LOC->$ conversion",
  },
  {
    id: "ux_research_lab",
    name: "UX Research Lab",
    category: "Meta Productivity Upgrades",
    costLoc: 1700,
    maxLevel: 4,
    description: "UX people improve LOC->$ conversion",
  },
  {
    id: "customer_feedback_loop",
    name: "Customer Feedback Loop",
    category: "Meta Productivity Upgrades",
    costLoc: 2300,
    maxLevel: 4,
    description: "Global conversion quality improves",
  },
  {
    id: "value_engineering",
    name: "Value Engineering",
    category: "Developer Upgrades",
    costLoc: 2600,
    maxLevel: 3,
    description: "Architects create higher-value LOC",
  },
  {
    id: "platform_teams",
    name: "Platform Teams",
    category: "Infrastructure Upgrades",
    costLoc: 2800,
    maxLevel: 4,
    description: "Improves team throughput and code value conversion",
  },
  {
    id: "enabling_teams",
    name: "Enabling Teams",
    category: "Meta Productivity Upgrades",
    costLoc: 2600,
    maxLevel: 4,
    description: "Cross-team coaching improves quality and reduces debt growth",
  },
  {
    id: "agent_swarm",
    name: "Agent Swarm",
    category: "AI-Native Delivery",
    costLoc: 3200,
    maxLevel: 3,
    description: "AI agents produce more LOC, but can increase bug pressure",
  },
  {
    id: "promptops_playbook",
    name: "PromptOps Playbook",
    category: "AI-Native Delivery",
    costLoc: 2900,
    maxLevel: 3,
    description: "Improves AI output quality and lowers AI operating costs",
  },
  {
    id: "eval_harness",
    name: "Eval Harness",
    category: "AI-Native Delivery",
    costLoc: 3400,
    maxLevel: 3,
    description: "Automated evals reduce AI-related defects",
  },
  {
    id: "golden_path_templates",
    name: "Golden Path Templates",
    category: "Platform Engineering",
    costLoc: 3000,
    maxLevel: 3,
    description: "Standardized templates speed delivery and cheaper onboarding",
  },
  {
    id: "internal_dev_portal",
    name: "Internal Dev Portal",
    category: "Platform Engineering",
    costLoc: 3500,
    maxLevel: 2,
    description: "Self-serve docs and tooling improve overall throughput",
  },
  {
    id: "self_service_environments",
    name: "Self-Service Environments",
    category: "Platform Engineering",
    costLoc: 3700,
    maxLevel: 2,
    description: "On-demand environments reduce wait states",
  },
  {
    id: "flow_efficiency_dashboard",
    name: "Flow Efficiency Dashboard",
    category: "Value Stream Management",
    costLoc: 3300,
    maxLevel: 3,
    description: "Flow metrics improve delivery speed and business value",
  },
  {
    id: "wip_limits",
    name: "WIP Limits",
    category: "Value Stream Management",
    costLoc: 2900,
    maxLevel: 3,
    description: "Lower multitasking overhead and quality regressions",
  },
  {
    id: "lead_time_alerts",
    name: "Lead Time Alerts",
    category: "Value Stream Management",
    costLoc: 3100,
    maxLevel: 3,
    description: "Detect bottlenecks early to prevent incidents",
  },
  {
    id: "stream_aligned_teams",
    name: "Stream-Aligned Teams",
    category: "Architecting for Flow",
    costLoc: 3600,
    maxLevel: 3,
    description: "Clear ownership improves flow and reduces coordination drag",
  },
  {
    id: "team_topologies_enabling",
    name: "Enabling Team Topologies",
    category: "Architecting for Flow",
    costLoc: 3400,
    maxLevel: 3,
    description: "Enabling teams reduce defects and technical friction",
  },
  {
    id: "platform_grouping",
    name: "Platform Grouping",
    category: "Architecting for Flow",
    costLoc: 3800,
    maxLevel: 2,
    description: "Right-sized platform boundaries increase team velocity",
  },
  {
    id: "wardley_map_session",
    name: "Wardley Map Session",
    category: "Wardley Mapping",
    costLoc: 3500,
    maxLevel: 3,
    description: "Better strategic positioning increases upgrade efficiency",
  },
  {
    id: "buy_vs_build_matrix",
    name: "Buy vs Build Matrix",
    category: "Wardley Mapping",
    costLoc: 3200,
    maxLevel: 3,
    description: "Make smarter platform bets and avoid costly detours",
  },
  {
    id: "commodity_migration",
    name: "Commodity Migration",
    category: "Wardley Mapping",
    costLoc: 3900,
    maxLevel: 2,
    description: "Commodity components free teams for higher-value work",
  },
  {
    id: "option_value_portfolio",
    name: "Option Value Portfolio",
    category: "Residuality",
    costLoc: 3600,
    maxLevel: 3,
    description: "Keep options open to reduce downside from surprises",
  },
  {
    id: "reversible_decisions",
    name: "Reversible Decisions",
    category: "Residuality",
    costLoc: 3300,
    maxLevel: 3,
    description: "Reversible architecture decisions lower strategic debt risk",
  },
  {
    id: "kill_switches",
    name: "Kill Switches",
    category: "Residuality",
    costLoc: 4100,
    maxLevel: 2,
    description: "Safety controls limit incident blast radius",
  },
];

export const PRESTIGE_UPGRADES: PrestigeUpgradeDefinition[] = [
  {
    id: "better_hiring_brand",
    name: "Better Hiring Brand",
    costReputation: 5,
    maxLevel: 12,
    description: "+4% developer LOC/sec per level",
  },
  {
    id: "engineering_culture",
    name: "Engineering Culture",
    costReputation: 8,
    maxLevel: 10,
    description: "+5% global output and click impact per level",
  },
  {
    id: "open_source_fame",
    name: "Open Source Fame",
    costReputation: 6,
    maxLevel: 10,
    description: "All hiring and upgrades get much cheaper",
  },
  {
    id: "momentum_bootstrap",
    name: "Momentum Bootstrap",
    costReputation: 9,
    maxLevel: 6,
    description: "New releases start with bonus $ and a warm team",
  },
  {
    id: "premium_contracts",
    name: "Premium Contracts",
    costReputation: 10,
    maxLevel: 8,
    description: "Higher $/LOC and stronger event monetization",
  },
  {
    id: "reliability_guild",
    name: "Reliability Guild",
    costReputation: 11,
    maxLevel: 6,
    description: "Big reductions to bug chance and severity",
  },
  {
    id: "platform_flywheel",
    name: "Platform Flywheel",
    costReputation: 10,
    maxLevel: 8,
    description: "Large boosts to passive and manual output",
  },
  {
    id: "release_multiplier",
    name: "Release Multiplier",
    costReputation: 12,
    maxLevel: 6,
    description: "Gain more reputation per release cycle",
  },
];

export const RANDOM_EVENTS: RandomEventDefinition[] = [
  {
    id: "production_outage",
    name: "Production Outage",
    description: "-50% LOC for 20s",
    durationMs: 20_000,
    productionMultiplier: 0.5,
  },
  {
    id: "viral_product",
    name: "Viral Product",
    description: "+200% LOC for 20s",
    durationMs: 20_000,
    productionMultiplier: 3,
  },
  {
    id: "hacker_news_post",
    name: "Hacker News Post",
    description: "Instant LOC burst",
    durationMs: 0,
    burstLoc: 250,
  },
  {
    id: "key_dev_resigns",
    name: "Key Dev Resigns",
    description: "One dev works at 0% for 25s",
    durationMs: 25_000,
    lostDevCount: 1,
  },
  {
    id: "dev_rewrites_component",
    name: "Dev Rewrites a Component",
    description: "Large rewrite happened: immediate LOC drop",
    durationMs: 10_000,
    locLossPercent: 0.22,
    locLossFlat: 80,
  },
  {
    id: "legacy_code_audit",
    name: "Legacy Code Audit",
    description: "Audit reveals hidden complexity: +tech debt",
    durationMs: 12_000,
    debtDelta: 160,
  },
  {
    id: "unexpected_refactor_win",
    name: "Unexpected Refactor Win",
    description: "A refactor paid off: debt reduced and LOC burst",
    durationMs: 8_000,
    debtDelta: -140,
    burstLoc: 180,
  },
  {
    id: "incident_war_room",
    name: "Incident War Room",
    description: "Team context switches to incident response",
    durationMs: 18_000,
    productionMultiplier: 0.7,
    debtDelta: 70,
  },
  {
    id: "automated_cleanup",
    name: "Automated Cleanup Script",
    description: "Cleanup script resolved some active bugs",
    durationMs: 6_000,
    clearBugs: 2,
  },
  {
    id: "mysterious_regression",
    name: "Mysterious Regression",
    description: "New regression introduced: additional bugs",
    durationMs: 10_000,
    addBugs: 2,
  },
  {
    id: "docs_week",
    name: "Docs Week",
    description: "Documentation sprint slows output, lowers debt",
    durationMs: 16_000,
    productionMultiplier: 0.85,
    debtDelta: -90,
  },
  {
    id: "hotfix_marathon",
    name: "Hotfix Marathon",
    description: "Big short-term LOC gain, but debt increases",
    durationMs: 12_000,
    burstLoc: 240,
    debtDelta: 190,
  },
];

export const TRADEOFF_MODES: TradeoffModeDefinition[] = [
  {
    id: "balanced",
    label: "Balanced",
    description: "No modifiers",
  },
  {
    id: "move_fast",
    label: "Move Fast",
    description: "+50% LOC, +100% bugs",
  },
  {
    id: "enterprise",
    label: "Enterprise Mode",
    description: "-20% LOC, -70% bugs",
  },
  {
    id: "rust_rewrite",
    label: "Rewrite in Rust",
    description: "Slower hiring, fewer bugs",
  },
  {
    id: "js_rewrite",
    label: "Rewrite in JavaScript",
    description: "Faster LOC, more bugs",
  },
  {
    id: "quality_sprint",
    label: "Quality Sprint",
    description: "-10% LOC, -35% bugs, +15% value",
  },
];

export const ABILITIES: AbilityDefinition[] = [
  {
    id: "crunch_time",
    unlockUpgradeId: "unlock_crunch_time",
    label: "Use Crunch Time",
    cooldownMs: 45_000,
  },
  {
    id: "hackathon",
    unlockUpgradeId: "unlock_hackathon",
    label: "Run Hackathon",
    cooldownMs: 60_000,
  },
  {
    id: "coffee_break",
    unlockUpgradeId: "unlock_coffee_break",
    label: "Start Coffee Break",
    cooldownMs: 40_000,
  },
  {
    id: "bug_bash",
    unlockUpgradeId: "unlock_bug_bash",
    label: "Start Bug Bash",
    cooldownMs: 50_000,
  },
];
